// UPLIFT — Cross-Sell AI for Agents.
//
// Ranked queue of the agent's highest-value next-policy opportunities.
// Per-card LLM-generated talk track loads lazily when the operator
// clicks an opportunity (keeps tokens cheap). Compliance gates
// (Medicare SOA, channel consent) enforced server-side; this view
// surfaces them visibly so the agent never accidentally skips one.
//
// Spec: docs/agents/uplift_system_prompt.md

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import {
  TrendingUp, Phone, MessageSquare, Mail, RefreshCcw, Loader2,
  ShieldAlert, ChevronRight, Sparkles, AlertTriangle,
} from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, init = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(init.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

function fmtCents(cents) {
  const n = Number(cents) || 0;
  if (n === 0) return '—';
  if (n >= 100000) return `$${(n / 100).toFixed(0)}`;
  return `$${(n / 100).toFixed(0)}/mo`;
}

function confidenceBadge(c) {
  const map = {
    high:   { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'HIGH' },
    medium: { bg: 'bg-amber-100',   text: 'text-amber-700',   label: 'MED' },
    low:    { bg: 'bg-slate-100',   text: 'text-slate-600',   label: 'LOW' },
  };
  const s = map[c] || map.low;
  return <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${s.bg} ${s.text}`}>{s.label}</span>;
}

function channelIcon(ch) {
  if (ch === 'sms')   return <MessageSquare size={11} className="inline mr-1"/>;
  if (ch === 'email') return <Mail size={11} className="inline mr-1"/>;
  return <Phone size={11} className="inline mr-1"/>;
}

export default function UpliftAgentView({ currentUser, showToast = () => {}, onGotoTab = () => {} }) {
  const [data, setData]       = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [vertical, setVertical] = React.useState('both');
  const [openId, setOpenId]   = React.useState(null);
  // Per-opportunity LLM card cache — once generated, don't re-call.
  const [cards, setCards] = React.useState({});

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/api/uplift/queue?vertical=${vertical}`);
      setData(r);
    } catch (err) {
      showToast(`UPLIFT load failed: ${err.message || err}`, 'error');
      setData({ ok: false, opportunities: [], summary: {} });
    } finally {
      setLoading(false);
    }
  }, [vertical, showToast]);
  React.useEffect(() => { load(); }, [load]);

  // Fetch the LLM card for a single opportunity. Cached by id.
  const fetchCard = React.useCallback(async (opp) => {
    if (cards[opp.id]) return; // already loaded
    setCards(prev => ({ ...prev, [opp.id]: { loading: true } }));
    try {
      const card = await apiFetch('/api/uplift/generate-card', {
        method: 'POST',
        body: JSON.stringify({ opportunity: opp }),
      });
      setCards(prev => ({ ...prev, [opp.id]: { loading: false, ...card } }));
    } catch (err) {
      setCards(prev => ({ ...prev, [opp.id]: { loading: false, error: err.message || String(err) } }));
    }
  }, [cards]);

  function call(opp) {
    if (!opp.phone) { showToast(`No phone on file for ${opp.name}.`, 'info'); return; }
    window.dispatchEvent(new CustomEvent('crm-dial-request', {
      detail: { lead: { id: opp.client_id, name: opp.name, phone: opp.phone, email: opp.email } },
    }));
  }

  function text(opp, body) {
    // Open the unified inbox + New text modal with body pre-filled.
    // The modal already handles ad-hoc phones — pass via a custom
    // event the inbox listens for.
    // Opens the global ad-hoc SMS composer (mounted at the app root)
    // with the number + drafted body pre-filled. No tab switch needed —
    // it's an overlay. Falls back gracefully if phone is missing.
    window.dispatchEvent(new CustomEvent('inbox:compose', {
      detail: { phone: opp.phone, name: opp.name, body, vertical: opp.is_medicare ? 'health' : null },
    }));
  }

  // Defensive summary — API error path can return summary: {} which
  // is truthy, so `||` alone doesn't populate defaults. Spread over
  // known-zero defaults so missing fields never crash toLocaleString().
  const summary = { opportunities: 0, medicare_gated: 0, unreachable: 0, weekly_premium_estimate_cents: 0, ...(data?.summary || {}) };
  const opps = data?.opportunities || [];

  return (
    <div className="max-w-none mx-auto px-4 py-4 space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-950 to-emerald-950/40 border border-emerald-900/30 rounded-xl p-5 text-white">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-emerald-400/80 flex items-center gap-2">
              <TrendingUp size={11}/> UPLIFT · cross-sell strategist
            </div>
            <h1 className="text-xl font-black mt-1.5">Your book's next best sale.</h1>
            <p className="text-xs text-slate-400 mt-1">
              {data?.is_demo
                ? 'Demo · synthesized queue showing the highest-value next-policy moves across the book.'
                : 'Daily scan of every active client. AI-generated talk track loads when you open a card.'}
            </p>
          </div>
          <button onClick={load} disabled={loading} className="text-xs text-emerald-200/70 hover:text-emerald-200 inline-flex items-center gap-1 disabled:opacity-30">
            <RefreshCcw size={11}/> Refresh
          </button>
        </div>

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-px bg-emerald-900/20 rounded-lg overflow-hidden">
          <Stat label="Opportunities" value={summary.opportunities.toLocaleString()} sub="this scan"/>
          <Stat label="Est. weekly"   value={fmtCents(summary.weekly_premium_estimate_cents)} sub="premium in queue"/>
          <Stat label="Medicare-gated" value={summary.medicare_gated.toLocaleString()} sub="SOA required"/>
          <Stat label="Unreachable"   value={summary.unreachable.toLocaleString()} sub="no consent / channel"/>
        </div>

        <div className="mt-4 inline-flex border border-emerald-900/30 rounded-full overflow-hidden">
          {[
            { key: 'both',   label: 'Both books' },
            { key: 'health', label: 'Health' },
            { key: 'life',   label: 'Life' },
          ].map(v => (
            <button
              key={v.key}
              onClick={() => setVertical(v.key)}
              className={`text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 ${vertical === v.key ? 'bg-emerald-500 text-black' : 'text-emerald-200/70 hover:text-emerald-200'}`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Queue */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        {loading && !data ? (
          <div className="text-center py-12 text-slate-500"><Loader2 size={20} className="animate-spin inline mr-2"/>Scanning the book…</div>
        ) : opps.length === 0 ? (
          <div className="text-center py-10 px-4">
            <Sparkles size={28} className="mx-auto text-slate-300 mb-2"/>
            <div className="font-bold text-slate-700">No cross-sell opportunities surfaced</div>
            <div className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
              Either every client is fully covered (unlikely) or the client_coverages table isn't populated. Run ANCHOR's cross-sell scan to fill it in.
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {opps.map((o, i) => (
              <OpportunityRow
                key={o.id}
                opp={o}
                rank={i + 1}
                isOpen={openId === o.id}
                onToggle={() => {
                  if (openId === o.id) { setOpenId(null); return; }
                  setOpenId(o.id);
                  fetchCard(o);
                }}
                card={cards[o.id]}
                onCall={() => call(o)}
                onText={(body) => text(o, body)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="bg-slate-950 px-3 py-2 text-center">
      <div className="text-2xl font-black tabular-nums text-emerald-400">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-0.5">{label}</div>
      {sub && <div className="text-[9px] text-slate-600 mt-0.5">{sub}</div>}
    </div>
  );
}

function OpportunityRow({ opp, rank, isOpen, onToggle, card, onCall, onText }) {
  return (
    <div className={`border rounded-lg overflow-hidden ${opp.soa_required ? 'border-amber-200' : 'border-slate-200'}`}>
      <button
        onClick={onToggle}
        className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center gap-3 ${opp.soa_required ? 'bg-amber-50/30' : ''}`}
      >
        <div className="text-[11px] font-bold text-slate-400 w-6 tabular-nums">#{rank}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-slate-900 text-sm truncate">{opp.name}</span>
            {opp.state && <span className="text-[9px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{opp.state}</span>}
            <span className="text-[9px] font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded uppercase tracking-wide">{opp.current_lob}</span>
            <span className="text-[10px] text-slate-400">→</span>
            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded uppercase tracking-wide">{opp.recommended_line}</span>
            {confidenceBadge(opp.confidence)}
            {opp.soa_required && (
              <span className="text-[9px] font-bold text-amber-800 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5">
                <ShieldAlert size={9}/> SOA REQUIRED
              </span>
            )}
            {!opp.reachable && (
              <span className="text-[9px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">UNREACHABLE</span>
            )}
          </div>
          {opp.triggers?.length > 0 && (
            <div className="text-[11px] text-slate-500 mt-0.5 truncate">
              ⚡ {opp.triggers.map(t => t.label).join(' · ')}
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-lg font-black text-emerald-700 tabular-nums">{opp.score}</div>
          <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">score</div>
        </div>
        <ChevronRight size={14} className={`text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}/>
      </button>

      {isOpen && (
        <div className="border-t border-slate-200 p-3 space-y-2 bg-slate-50/50">
          {opp.soa_warning && (
            <div className="text-[12px] text-amber-900 bg-amber-100 border border-amber-300 rounded px-2.5 py-2 inline-flex items-start gap-2">
              <ShieldAlert size={14} className="shrink-0 mt-0.5"/>
              <span><strong>Medicare gate:</strong> {opp.soa_warning}</span>
            </div>
          )}
          {!card || card.loading ? (
            <div className="text-xs text-slate-500"><Loader2 size={12} className="animate-spin inline mr-1.5"/>UPLIFT is drafting your talk track…</div>
          ) : card.error ? (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">
              <AlertTriangle size={11} className="inline mr-1"/>
              Card draft failed: {card.error}. The compliance gate above is still in force.
            </div>
          ) : (
            <>
              {card.reason && (
                <div className="text-[12px] text-slate-700">
                  <span className="font-bold text-slate-500 uppercase tracking-wider text-[9px] mr-1.5">Why:</span>
                  {card.reason}
                </div>
              )}
              <div className="bg-white border border-slate-200 rounded px-3 py-2.5">
                <div className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Suggested talk track</div>
                <div className="text-sm text-slate-900 leading-relaxed">{card.talk_track}</div>
              </div>
              {card.fallback && (
                <div className="text-[11px] text-amber-700">
                  ⚠ AI draft unavailable ({card.fallback_reason}) — using template.
                </div>
              )}
              {Array.isArray(card.warnings) && card.warnings.length > 0 && (
                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  {card.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap pt-1">
                <button
                  onClick={onCall}
                  disabled={!opp.phone}
                  className="inline-flex items-center gap-1 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 px-2.5 py-1.5 rounded"
                >
                  {channelIcon('phone')} Call now
                </button>
                {opp.channel === 'sms' && (
                  <button
                    onClick={() => onText(card.talk_track)}
                    className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-white border border-emerald-300 hover:bg-emerald-50 px-2.5 py-1.5 rounded"
                  >
                    {channelIcon('sms')} Draft text with talk track
                  </button>
                )}
                <span className="text-[10px] text-slate-500 ml-1">
                  Recommended channel: {channelIcon(card.channel_recommended)}{card.channel_recommended || 'phone'}
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
